import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { store } from './store';
import { theme } from './utils/theme';
import { fetchProfile } from './store/slices/authSlice';

import Layout from './components/common/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import UnitTrackerPage from './pages/UnitTrackerPage';
import SalesPage from './pages/SalesPage';
import SalesDetailPage from './pages/SalesDetailPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import WarehousesPage from './pages/WarehousesPage';
import WarehouseDetailPage from './pages/WarehouseDetailPage';
import InventoryPage from './pages/InventoryPage';
import InvoicesPage from './pages/InvoicesPage';
import UsersPage from './pages/UsersPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import PortfolioPage from './pages/PortfolioPage';
import StaffPage from './pages/StaffPage';
import StaffDetailPage from './pages/StaffDetailPage';
import PayrollPage from './pages/PayrollPage';
import SparePartsDashboard from './pages/sp/SparePartsDashboard';
import SparePartsProducts from './pages/sp/SparePartsProducts';
import SparePartsOrders from './pages/sp/SparePartsOrders';
import SparePartsCustomers from './pages/sp/SparePartsCustomers';
import SparePartsInventory from './pages/sp/SparePartsInventory';
import SparePartsSuppliersPage from './pages/sp/SparePartsSuppliers';
import SparePartsOrderDetailPage from './pages/sp/SparePartsOrderDetailPage';
import { getHomePathForRoles, hasAnyRole } from './utils/roles';

const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, user } = useSelector(s => s.auth);
  const { active } = useSelector(s => s.system);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user && !hasAnyRole(user.roles, roles)) {
    return <Navigate to={getHomePathForRoles(user.roles, active)} replace />;
  }
  return children;
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(s => s.auth);

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchProfile());
  }, [dispatch, isAuthenticated]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PortfolioPage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Protected admin/internal routes for Car Sales */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
      </Route>
      <Route path="/products" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<ProductsPage />} />
        <Route path=":id" element={<ProductDetailPage />} />
      </Route>
      <Route path="/unit-tracker" element={<ProtectedRoute roles={['admin','manager','storekeeper']}><Layout /></ProtectedRoute>}>
        <Route index element={<UnitTrackerPage />} />
      </Route>
      <Route path="/sales" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<SalesPage />} />
        <Route path=":id" element={<SalesDetailPage />} />
      </Route>
      <Route path="/customers" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<CustomersPage />} />
        <Route path=":id" element={<CustomerDetailPage />} />
      </Route>
      <Route path="/suppliers" element={<ProtectedRoute roles={['admin','manager','storekeeper']}><Layout /></ProtectedRoute>}>
        <Route index element={<SuppliersPage />} />
      </Route>
      <Route path="/purchase-orders" element={<ProtectedRoute roles={['admin','manager','storekeeper']}><Layout /></ProtectedRoute>}>
        <Route index element={<PurchaseOrdersPage />} />
      </Route>
      <Route path="/warehouses" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<WarehousesPage />} />
        <Route path=":id" element={<WarehouseDetailPage />} />
      </Route>
      <Route path="/inventory" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<InventoryPage />} />
      </Route>
      <Route path="/invoices" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<InvoicesPage />} />
      </Route>
      <Route path="/staffs" element={<ProtectedRoute roles={['admin','manager']}><Layout /></ProtectedRoute>}>
        <Route index element={<StaffPage />} />
        <Route path=":id" element={<StaffDetailPage />} />
      </Route>
      <Route path="/payroll" element={<ProtectedRoute roles={['admin','manager','accountant']}><Layout /></ProtectedRoute>}>
        <Route index element={<PayrollPage />} />
      </Route>
      <Route path="/reports" element={<ProtectedRoute roles={['admin','manager','accountant']}><Layout /></ProtectedRoute>}>
        <Route index element={<ReportsPage />} />
      </Route>
      <Route path="/users" element={<ProtectedRoute roles={['admin']}><Layout /></ProtectedRoute>}>
        <Route index element={<UsersPage />} />
      </Route>
      <Route path="/settings" element={<ProtectedRoute roles={['admin']}><Layout /></ProtectedRoute>}>
        <Route index element={<SettingsPage />} />
      </Route>
      <Route path="/profile" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<ProfilePage />} />
      </Route>

      {/* Spare Parts Routes (dedicated components) */}
      <Route path="/sp" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/sp/dashboard" replace />} />
        <Route path="dashboard" element={<SparePartsDashboard />} />
        <Route path="products" element={<SparePartsProducts />} />
        <Route path="inventory" element={<SparePartsInventory />} />
        <Route path="orders" element={<SparePartsOrders />} />
        <Route path="orders/:id" element={<SparePartsOrderDetailPage />} />
        <Route path="customers" element={<SparePartsCustomers />} />
        <Route path="suppliers" element={<SparePartsSuppliersPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const App = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop />
    </ThemeProvider>
  </Provider>
);

export default App;
